import moment from 'moment'
import { useAuthStore } from '@/pinia/auth'
import { useSettingStore } from '@/pinia/setting'

/**
 * WhatsApp Integration Service for Dekacare
 * Handles WhatsApp Web/Mobile integration for sharing receipts and documents
 */
class WhatsAppService {
  constructor() {
    this.baseUrls = {
      web: 'https://api.whatsapp.com/send',
      mobile: 'whatsapp://send'
    }

    // Indonesia country code
    this.countryCode = '62'

    // Default message templates - all use {facilityFooter} for unified footer
    this.messageTemplates = {
      eresep: 'Resep untuk: {patientName}\nNo. RM: {norm}\nTanggal: {date}\n\nSilakan lihat lampiran PDF untuk detail resep.\n\nTerima kasih,\n{facilityFooter}',
      invoice: 'Invoice untuk: {patientName}\nNo. RM: {norm}\n\n{rincianBiaya}\n\nTotal: {total}\nTanggal: {date}.\n\nTerima kasih,\n{facilityFooter}',
      copyresep: 'Copy Resep untuk: {patientName}\nNo. RM: {norm}\nTanggal: {date}.\n\nTerima kasih,\n{facilityFooter}',
      jadwalkontrol: 'Ini merupakan pesan resmi yang kami kirimkan dari akun Whatsapp resmi *{facilityName}*.\n\nBersama ini kami sampaikan Jadwal kontrol anda selanjutnya :\n\nNama Pasien     : {patientName}\nTanggal/Jam     : {jadwalDate}\nNo. Kontrol     : {noKontrol}\nTujuan          : {poli}\nDokter          : {dokter}\n\nCATATAN PENTING\n\nMohon Bapak/Ibu datang kontrol SESUAI dengan jadwal kontrol yang telah dijadwalkan.\n\nJika ada kendala/pertanyaan/ silahkan hubungi kami di {facilityPhone}.\n\nTerima kasih,\n{facilityFooter}',
      birthday: 'Kepada Bapak/Ibu {patientName}, yang kami cintai, Selamat ulang tahun. Doa dan harapan terbaik untuk hari bahagia Anda!\n\nKami segenap manajemen beserta seluruh staf *{facilityName}* mengucapkan salam terhangat di hari Ulang Tahun Anda ini.\n\nSemoga tahun ini menjadi tahun yang membawa kesuksesan, kebahagiaan dan kesehatan untuk Anda, keluarga dan seluruh orang-orang yang Anda kasihi. Amin\n\nSalam Sehat,\n{facilityFooter}',
      hasillab: '🧪 *HASIL PEMERIKSAAN LABORATORIUM*\n\nHalo Bapak/Ibu *{patientName}* 👋\nBerikut hasil pemeriksaan laboratorium Anda yang dilakukan pada *{examDate}* di *{facilityName}*.\n\n────────────────────\n📋 *Daftar Pemeriksaan:*\n\n{labResults}\n────────────────────\n\n🩺 *Catatan Dokter:*\n{doctorNote}\n\nMohon hasil ini digunakan sebagai informasi awal saja.\nUntuk interpretasi lebih lanjut, silakan konsultasi dengan dokter Anda.\n\nTerima kasih 🙏\n{facilityFooter}',
      kwitansi: 'Halo {patientName},\n\nKami lampirkan kwitansi kunjungan Anda dari *{facilityName}*, sebagai berikut:\n------------------------------------\nNo. Nota: {nomorKunjungan}\nTanggal Masuk: {tanggalMasuk}\nTotal: {totalPrice}\n\nRincian:\n{rincianItems}\n------------------------------------\n*Total*: {totalPrice}\n{facilityFooter}',
      penjualan: 'Pelanggan *{facilityName}* yang kami cintai,\n\nBerikut struk bukti pembelian Anda:\nNo.Nota: {invoiceNumber}\nTanggal: {date}\n\nItem:\n{itemList}\n\n{rincianBiaya}\n\n*Total: {totalAmount}*\n{facilityFooter}',
      klaimapproval: 'Halo *{approverNama}*,\n\nKami dari *{facilityName}* mengajukan permohonan persetujuan klaim berikut:\n\n📄 *No. Klaim*: {kodeKlaim}\n📅 *Periode*: {periode}\n💰 *Total*: {totalKeseluruhan}\n\nMohon Bapak/Ibu berkenan meninjau dan menandatangani klaim melalui tautan berikut:\n\n{approvalUrl}\n\nTerima kasih atas perhatian dan kerja samanya.\n\nSalam hormat,\n{makerNama}\n{facilityFooter}',
      general: 'Dokumen dari {facilityName}\n\nUntuk: {patientName}\nTanggal: {date}\n\nSilakan lihat lampiran PDF.\n\nTerima kasih.\n{facilityFooter}'
    }
  }

  /**
   * Format phone number from local database (auto-convert 0 prefix to 62)
   */
  formatPhoneNumberFromLocal(phone) {
    if (!phone) return null

    // Convert to string and clean
    let cleanPhone = String(phone).replace(/\D/g, '')

    // Auto-replace leading 0 with 62 for Indonesian numbers
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '62' + cleanPhone.substring(1)
    }

    return cleanPhone
  }

  /**
   * Format phone number for WhatsApp (Indonesian format)
   */
  formatPhoneNumber(phone) {
    if (!phone) return null

    // Remove all non-numeric characters
    let cleanPhone = phone.replace(/\D/g, '')

    // Handle Indonesian phone numbers
    if (cleanPhone.startsWith('0')) {
      // Replace leading 0 with country code
      cleanPhone = this.countryCode + cleanPhone.substring(1)
    } else if (cleanPhone.startsWith('8')) {
      // Add country code if starts with 8
      cleanPhone = this.countryCode + cleanPhone
    } else if (!cleanPhone.startsWith(this.countryCode)) {
      // Add country code if not present
      cleanPhone = this.countryCode + cleanPhone
    }

    // Validate length (Indonesian mobile: 10-13 digits including country code)
    if (cleanPhone.length < 10 || cleanPhone.length > 15) {
      throw new Error('Nomor telepon tidak valid')
    }

    return cleanPhone
  }

  /**
   * Validate phone number
   */
  isValidPhoneNumber(phone) {
    try {
      const formatted = this.formatPhoneNumber(phone)
      return formatted !== null
    } catch (error) {
      return false
    }
  }

  /**
   * Build unified facility footer with debug logging
   * Structure:
   *   {rewardPointsSection}
   *   _{footerText}_ (from setting: motto, custom text, or nothing)
   *   🏥 *{facilityName}*
   *   📞 {facilityPhone}
   */
  buildFacilityFooter(options = {}) {
    const {
      rewardPoints = null,
      entityData = null,
      callerName = 'unknown'
    } = options

    // Get entity data from store if not provided
    const entity = entityData || this.getEntityData()

    // Get footer text based on setting
    const footerText = this.getFooterText(entity)

    // Debug: Log the source and data
    console.log(`[WhatsApp Footer Debug] Called from: ${callerName}`)
    console.log('[WhatsApp Footer Debug] Entity source:', entityData ? 'passed' : 'from store')
    console.log('[WhatsApp Footer Debug] Entity data:', JSON.stringify({
      name: entity?.name || null,
      phone: entity?.phone || null,
      motto: entity?.motto || null,
      footerText: footerText,
      hasEntity: !!entity,
    }, null, 2))

    // Build reward points section
    let footer = ''
    if (rewardPoints && rewardPoints.earned && rewardPoints.earned > 0) {
      footer += '\n------------------------------------\n🎁 *POIN REWARD*\n'
      footer += `Poin Diraih: *+${rewardPoints.earned} poin*\n`
      if (rewardPoints.currentBalance !== undefined) {
        footer += `Saldo Poin: ${rewardPoints.currentBalance} poin\n`
      }
      if (rewardPoints.tier) {
        footer += `Tingkatan: *${rewardPoints.tier}*\n`
      }
      footer += '------------------------------------\n'
    }

    // Add footer text (italic) - from setting: motto, custom text, or nothing
    if (footerText) {
      footer += `\n_${footerText}_\n`
    }

    // Add facility name (bold) and phone
    const name = entity?.name || ''
    const phone = entity?.phone || '-'

    footer += `🏥 *${name}*\n`
    footer += `📞 ${phone}`

    console.log('[WhatsApp Footer Debug] Final footer:', footer)

    return footer
  }

  /**
   * Get footer text based on setting 'cetakan:eresep:teks-footer'
   * - '[Motto Faskes]' -> use entity.motto
   * - '[Tanpa Teks]' -> empty string
   * - Custom text -> use that text
   */
  getFooterText(entityData = null) {
    try {
      const settingStore = useSettingStore()
      const footerSetting = settingStore.val('cetakan:eresep:teks-footer')

      console.log('[WhatsApp Footer Debug] Footer setting:', footerSetting)

      if (!footerSetting || footerSetting === '[Tanpa Teks]') {
        return ''
      }

      if (footerSetting === '[Motto Faskes]') {
        const entity = entityData || this.getEntityData()
        return entity?.motto || ''
      }

      // Return custom text from setting
      return footerSetting
    } catch (error) {
      console.error('Failed to get footer text:', error)
      return ''
    }
  }

  /**
   * Generate message from template
   */
  generateMessage(template, data) {
    const templates = this.messageTemplates
    const messageTemplate = templates[template] || templates.general

    // Replace placeholders with actual data
    return messageTemplate
      .replace(/{patientName}/g, data.patientName || 'Pasien')
      .replace(/{norm}/g, data.norm || 'N/A')
      .replace(/{date}/g, data.date || moment().format('DD/MM/YYYY HH:mm'))
      .replace(/{total}/g, data.total || '')
      .replace(/{facilityName}/g, data.facilityName || 'Klinik')
      .replace(/{facilityPhone}/g, data.facilityPhone || '-')
      .replace(/{facilityAddress}/g, data.facilityAddress || '')
      .replace(/{facilityMotto}/g, data.facilityMotto || '')
      .replace(/{facilityFooter}/g, data.facilityFooter || '')
      .replace(/{jadwalDate}/g, data.jadwalDate || '')
      .replace(/{noKontrol}/g, data.noKontrol || '-')
      .replace(/{poli}/g, data.poli || '-')
      .replace(/{dokter}/g, data.dokter || '-')
      .replace(/{examDate}/g, data.examDate || moment().format('DD/MM/YYYY'))
      .replace(/{labResults}/g, data.labResults || '-')
      .replace(/{doctorNote}/g, data.doctorNote || '_Tidak ada catatan khusus_')
      .replace(/{nomorKunjungan}/g, data.nomorKunjungan || '-')
      .replace(/{tanggalMasuk}/g, data.tanggalMasuk || '-')
      .replace(/{totalPrice}/g, data.totalPrice || 'Rp. 0')
      .replace(/{rincianItems}/g, data.rincianItems || '-')
      .replace(/{rewardPointsSection}/g, data.rewardPointsSection || '')
      .replace(/{invoiceNumber}/g, data.invoiceNumber || '-')
      .replace(/{itemList}/g, data.itemList || '-')
      // Optional block. Surrounding newlines are absorbed so an empty
      // breakdown collapses to a single separator instead of a run of blanks.
      .replace(
        /\n*{rincianBiaya}\n*/g,
        data.rincianBiaya ? `\n\n${data.rincianBiaya}\n\n` : '\n\n'
      )
      .replace(/{totalAmount}/g, data.totalAmount || 'Rp. 0')
      .replace(/{approverNama}/g, data.approverNama || 'Bapak/Ibu Approver')
      .replace(/{kodeKlaim}/g, data.kodeKlaim || '-')
      .replace(/{periode}/g, data.periode || '-')
      .replace(/{totalKeseluruhan}/g, data.totalKeseluruhan || '-')
      .replace(/{approvalUrl}/g, data.approvalUrl || '')
      .replace(/{makerNama}/g, data.makerNama || '-')
  }

  /**
   * Detect if user is on mobile device
   */
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  /**
   * Get appropriate WhatsApp URL based on device
   */
  getWhatsAppUrl(phone, message, forceMobile = false) {
    const formattedPhone = this.formatPhoneNumber(phone)
    const encodedMessage = encodeURIComponent(message)

    const isMobile = forceMobile || this.isMobileDevice()
    const baseUrl = isMobile ? this.baseUrls.mobile : this.baseUrls.web

    return `${baseUrl}?phone=${formattedPhone}&text=${encodedMessage}`
  }

  /**
   * Open WhatsApp with message
   */
  openWhatsApp(phone, message, options = {}) {
    try {
      const url = this.getWhatsAppUrl(phone, message, options.forceMobile)

      if (options.newWindow !== false) {
        // Open in new window/tab
        const whatsappWindow = window.open(url, '_blank', 'noopener,noreferrer')

        if (!whatsappWindow) {
          throw new Error('Popup blocked. Please allow popups for this site.')
        }

        return {
          success: true,
          url: url,
          method: 'popup'
        }
      } else {
        // Redirect current window
        window.location.href = url
        return {
          success: true,
          url: url,
          method: 'redirect'
        }
      }
    } catch (error) {
      console.error('WhatsApp opening failed:', error)
      throw error
    }
  }

  /**
   * Send e-resep via WhatsApp
   */
  async sendEResep(phone, eresepData, options = {}) {
    try {
      // Get entity data from store if not provided
      const entityData = eresepData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints: null,
        entityData,
        callerName: 'sendEResep'
      })

      const messageData = {
        patientName: eresepData?.kunjungan?.pendaftaran?.pasien?.nama ||
                    eresepData?.pasien?.nama ||
                    'Pasien',
        norm: eresepData?.kunjungan?.pendaftaran?.norm ||
              eresepData?.pasien?.no_rm ||
              'N/A',
        date: moment().format('DD/MM/YYYY HH:mm'),
        facilityName: entityData?.name || 'Klinik',
        facilityPhone: entityData?.phone || '-',
        facilityFooter
      }

      const template = eresepData?.isInvoice ? 'invoice' : 'eresep'

      if (template === 'invoice' && eresepData?.grandTotalWithAdjustments) {
        messageData.total = new Intl.NumberFormat('id-ID', {
          style: 'currency',
          currency: 'IDR',
          minimumFractionDigits: 0
        }).format(eresepData.grandTotalWithAdjustments)
        messageData.rincianBiaya = this.formatRincianBiaya(eresepData.rincian)
      }

      const message = this.generateMessage(template, messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('E-Resep WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Send copy resep via WhatsApp
   */
  async sendCopyResep(phone, resepData, options = {}) {
    try {
      // Get entity data from store if not provided
      const entityData = resepData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints: null,
        entityData,
        callerName: 'sendCopyResep'
      })

      const messageData = {
        patientName: resepData?.kunjungan?.pendaftaran?.pasien?.nama ||
                    resepData?.pasien?.nama ||
                    'Pasien',
        norm: resepData?.kunjungan?.pendaftaran?.norm ||
              resepData?.pasien?.no_rm ||
              'N/A',
        date: moment().format('DD/MM/YYYY HH:mm'),
        facilityName: entityData?.name || 'Klinik',
        facilityFooter
      }

      const message = this.generateMessage('copyresep', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Copy Resep WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Send jadwal kontrol reminder via WhatsApp
   */
  async sendJadwalKontrol(phone, jadwalData, options = {}) {
    try {
      // Get entity data from store if not provided
      const entityData = jadwalData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints: null,
        entityData,
        callerName: 'sendJadwalKontrol'
      })

      const messageData = {
        patientName: jadwalData?.pendaftaran?.pasien?.nama || 'Pasien',
        norm: jadwalData?.pendaftaran?.norm || 'N/A',
        jadwalDate: jadwalData?.jadwalDate || '',
        noKontrol: jadwalData?.no_kontrol || '-',
        poli: jadwalData?.poli?.deskripsi || '-',
        dokter: jadwalData?.dokter?.pegawai?.nama || '-',
        facilityName: entityData?.name || 'Klinik',
        facilityPhone: entityData?.phone || '-',
        facilityAddress: entityData?.address || '',
        facilityFooter
      }

      const message = this.generateMessage('jadwalkontrol', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Jadwal Kontrol WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Send general document via WhatsApp
   */
  async sendDocument(phone, documentData, options = {}) {
    try {
      // Get entity data from store if not provided
      const entityData = documentData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints: null,
        entityData,
        callerName: 'sendDocument'
      })

      const messageData = {
        patientName: documentData?.patientName || 'Pasien',
        norm: documentData?.norm || 'N/A',
        date: moment().format('DD/MM/YYYY HH:mm'),
        facilityName: entityData?.name || 'Klinik',
        facilityFooter
      }

      const message = this.generateMessage('general', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Document WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Show phone number input dialog
   */
  async promptPhoneNumber(defaultPhone = '') {
    return new Promise((resolve, reject) => {
      // Create modal or use SweetAlert2 if available
      if (window.Swal) {
        window.Swal.fire({
          title: 'Kirim ke WhatsApp',
          input: 'tel',
          inputLabel: 'Nomor WhatsApp',
          inputValue: defaultPhone,
          inputPlaceholder: '08123456789',
          inputAttributes: {
            'aria-label': 'Nomor WhatsApp'
          },
          showCancelButton: true,
          confirmButtonText: 'Kirim',
          cancelButtonText: 'Batal',
          inputValidator: (value) => {
            if (!value) {
              return 'Nomor WhatsApp harus diisi!'
            }
            if (!this.isValidPhoneNumber(value)) {
              return 'Format nomor WhatsApp tidak valid!'
            }
          }
        }).then((result) => {
          if (result.isConfirmed) {
            resolve(result.value)
          } else {
            reject(new Error('Dibatalkan oleh user'))
          }
        })
      } else {
        // Fallback to browser prompt. Cancel and bad input are reported
        // separately so the caller can tell "user backed out" from "typo".
        const phone = window.prompt('Masukkan nomor WhatsApp:', defaultPhone)
        if (phone === null || phone.trim() === '') {
          reject(new Error('Dibatalkan oleh user'))
        } else if (!this.isValidPhoneNumber(phone)) {
          reject(new Error('Nomor WhatsApp tidak valid'))
        } else {
          resolve(phone)
        }
      }
    })
  }

  /**
   * Get patient phone number from data
   */
  extractPatientPhone(patientData) {
    // Debug: Log the incoming data structure
    console.log('[extractPatientPhone] Input data:', JSON.stringify({
      hasData: !!patientData,
      hasPasien: !!patientData?.pasien,
      hasKontakPasien: !!patientData?.pasien?.kontak_pasien,
      kontakPasienLength: patientData?.pasien?.kontak_pasien?.length,
      kontakPasien: patientData?.pasien?.kontak_pasien
    }, null, 2))

    // Try to get phone from various possible locations in patient data
    const possiblePhoneFields = [
      'phone',
      'telepon',
      'no_hp',
      'no_telepon',
      'handphone',
      'hp'
    ]

    if (!patientData) return null

    // Helper function to extract phone from kontak_pasien array
    const extractFromKontakPasien = (kontakArray) => {
      console.log('[extractPatientPhone] extractFromKontakPasien called with:', kontakArray)
      if (!kontakArray || !Array.isArray(kontakArray)) return null

      // First, try to find jenis 4 (mobile phone) - check both number and string
      const mobileKontak = kontakArray.find((kontak) => (kontak.jenis === 4 || kontak.jenis === '4') && kontak.nomor)
      console.log('[extractPatientPhone] Found mobile kontak (jenis 4):', mobileKontak)
      if (mobileKontak) {
        return this.formatPhoneNumberFromLocal(mobileKontak.nomor)
      }

      // If no jenis 4, try any other contact with nomor
      const anyKontak = kontakArray.find((kontak) => kontak.nomor)
      console.log('[extractPatientPhone] Found any kontak with nomor:', anyKontak)
      if (anyKontak) {
        return this.formatPhoneNumberFromLocal(anyKontak.nomor)
      }

      return null
    }

    // Check direct properties
    for (const field of possiblePhoneFields) {
      if (patientData[field]) {
        return this.formatPhoneNumberFromLocal(patientData[field])
      }
    }

    // Check nested objects
    if (patientData.pasien) {
      for (const field of possiblePhoneFields) {
        if (patientData.pasien[field]) {
          return this.formatPhoneNumberFromLocal(patientData.pasien[field])
        }
      }

      // Check kontak_pasien array for phone numbers
      const phoneFromKontak = extractFromKontakPasien(patientData.pasien.kontak_pasien)
      if (phoneFromKontak) return phoneFromKontak
    }

    if (patientData.kunjungan && patientData.kunjungan.pendaftaran && patientData.kunjungan.pendaftaran.pasien) {
      const pasien = patientData.kunjungan.pendaftaran.pasien

      for (const field of possiblePhoneFields) {
        if (pasien[field]) {
          return this.formatPhoneNumberFromLocal(pasien[field])
        }
      }

      // Check kontak_pasien array for phone numbers
      const phoneFromKontak = extractFromKontakPasien(pasien.kontak_pasien)
      if (phoneFromKontak) return phoneFromKontak
    }

    // Check if patientData itself has pendaftaran.pasien structure (for jadwal kontrol data)
    if (patientData.pendaftaran && patientData.pendaftaran.pasien) {
      const pasien = patientData.pendaftaran.pasien

      for (const field of possiblePhoneFields) {
        if (pasien[field]) {
          return this.formatPhoneNumberFromLocal(pasien[field])
        }
      }

      // Check kontak_pasien array for phone numbers
      const phoneFromKontak = extractFromKontakPasien(pasien.kontak_pasien)
      if (phoneFromKontak) return phoneFromKontak
    }

    return null
  }

  /**
   * Complete workflow: PDF + WhatsApp
   */
  async sendPDFViaWhatsApp(pdfBlob, patientData, messageType = 'general', options = {}) {
    try {
      // Get phone number
      const existingPhone = this.extractPatientPhone(patientData)
      const phone = options.phone || existingPhone || await this.promptPhoneNumber(existingPhone)

      // Generate and send message
      let result
      switch (messageType) {
      case 'eresep':
        result = await this.sendEResep(phone, patientData, options)
        break
      case 'copyresep':
        result = await this.sendCopyResep(phone, patientData, options)
        break
      case 'jadwalkontrol':
        result = await this.sendJadwalKontrol(phone, patientData, options)
        break
      default:
        result = await this.sendDocument(phone, patientData, options)
      }

      return {
        ...result,
        phoneUsed: phone,
        pdfSize: pdfBlob ? `${(pdfBlob.size / 1024).toFixed(2)} KB` : 'Unknown'
      }
    } catch (error) {
      console.error('PDF WhatsApp workflow failed:', error)
      throw error
    }
  }

  /**
   * Check WhatsApp availability
   */
  isWhatsAppAvailable() {
    // Basic check - WhatsApp Web should be accessible
    return navigator.onLine && (
      this.isMobileDevice() || // Mobile devices usually have WhatsApp
      window.location.protocol === 'https:' // WhatsApp Web requires HTTPS
    )
  }

  /**
   * Send birthday wishes via WhatsApp
   */
  async sendBirthdayWishes(phone, birthdayData, options = {}) {
    try {
      // Get entity data from store if not provided
      const entityData = birthdayData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints: null,
        entityData,
        callerName: 'sendBirthdayWishes'
      })

      const messageData = {
        patientName: birthdayData?.nama || birthdayData?.patientName || 'Pasien',
        facilityName: entityData?.name || 'Klinik',
        facilityAddress: entityData?.address || entityData?.alamat || '',
        facilityFooter
      }

      const message = this.generateMessage('birthday', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Birthday WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Format lab results for WhatsApp message
   */
  formatLabResults(tindakanLab, parametersCache) {
    if (!tindakanLab || tindakanLab.length === 0) return '-'

    let labResultsText = ''

    tindakanLab.forEach((tindakan, index) => {
      // Get parameters for this tindakan
      const parameters = parametersCache[tindakan.no_detail] || []

      // Filter only parameters with hasil filled
      const filledParameters = parameters.filter(p => p.hasil && p.hasil.trim() !== '')

      if (filledParameters.length > 0) {
        // Add tindakan name
        labResultsText += `*Tindakan:* ${tindakan.tindakan.nama}\n`

        // Add each parameter
        filledParameters.forEach(param => {
          labResultsText += `• ${param.parameter} : *${param.hasil}*`

          // Add unit if available
          if (param.satuan) {
            labResultsText += ` ${param.satuan}`
          }

          labResultsText += '\n'

          // Add normal value if available
          if (param.nilai) {
            labResultsText += `  _(Nilai Normal: ${param.nilai})_\n`
          }

          // Add note if available
          if (param.keterangan && param.keterangan.trim() !== '') {
            labResultsText += `  _Catatan: ${param.keterangan}_\n`
          }
        })

        // Add spacing between tindakan (except last one)
        if (index < tindakanLab.length - 1) {
          labResultsText += '\n'
        }
      }
    })

    return labResultsText || '_Tidak ada hasil yang diinput_'
  }

  /**
   * Send lab results via WhatsApp
   */
  async sendHasilLab(phone, labData, options = {}) {
    try {
      // Get entity data from store if not provided
      const entityData = labData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints: null,
        entityData,
        callerName: 'sendHasilLab'
      })

      // Format lab results text
      const labResultsText = this.formatLabResults(labData.tindakanLab, labData.parametersCache)

      const messageData = {
        patientName: labData.kunjungan?.pendaftaran?.pasien?.nama || 'Pasien',
        examDate: labData.examDate || moment().format('DD/MM/YYYY'),
        facilityName: entityData?.name || 'Klinik',
        facilityPhone: entityData?.phone || '-',
        labResults: labResultsText,
        doctorNote: labData.doctorNote || '_Tidak ada catatan khusus_',
        facilityFooter
      }

      const message = this.generateMessage('hasillab', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Hasil Lab WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Format money for display (Indonesian format)
   */
  formatMoney(value, fallback = 'Rp. 0') {
    if (!value && value !== 0) return fallback

    const num = parseFloat(value)
    if (isNaN(num)) return fallback

    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num)
  }

  /**
   * Group summary list by unit_pelayanan
   */
  groupSummaryByUnit(summaryList) {
    if (!summaryList || !Array.isArray(summaryList)) return []

    // Use lodash-like grouping
    const groups = {}
    summaryList.forEach(item => {
      const key = item.unit_pelayanan || 'Lainnya'
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(item)
    })

    // Calculate totals
    const result = []
    Object.keys(groups).forEach(key => {
      const total = groups[key].reduce((acc, item) => acc + parseFloat(item.total || 0), 0)
      result.push({ key, total })
    })

    return result
  }

  /**
   * Send kwitansi (invoice receipt) via WhatsApp
   */
  async sendKwitansi(phone, kwitansiData, options = {}) {
    try {
      const { kunjungan, summaryList, pembulatan, rewardPoints } = kwitansiData

      // Get entity data from store if not provided
      const entityData = kwitansiData?.entityData || this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints,
        entityData,
        callerName: 'sendKwitansi'
      })

      // Group items and calculate total
      const groupedItems = this.groupSummaryByUnit(summaryList)
      let total = parseFloat(pembulatan || 0)

      // Build rincian items text
      let rincianItems = ''
      groupedItems.forEach((item, index) => {
        total += parseFloat(item.total || 0)
        rincianItems += `${index + 1}. ${item.key} => ${this.formatMoney(item.total)}\n`
      })

      // Prepare message data
      const messageData = {
        patientName: kunjungan?.pendaftaran?.pasien?.nama || 'Pasien',
        facilityName: entityData?.name || 'Klinik',
        nomorKunjungan: kunjungan?.nomor_kunjungan || '-',
        tanggalMasuk: moment(kunjungan?.masuk).format('DD/MMMM/YYYY'),
        totalPrice: this.formatMoney(total),
        rincianItems: rincianItems.trim(),
        facilityFooter
      }

      const message = this.generateMessage('kwitansi', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Kwitansi WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Send klaim approval link via WhatsApp.
   * Returns { needsPhoneInput: true, message } if no phone resolvable,
   * else returns the openWhatsApp result (the link is opened in a new tab).
   */
  sendKlaimApprovalWA({ klaim, approverPegawai = null, approvalUrl, makerNama, phone = null, facilityName = null, facilityFooter = null, options = {} } = {}) {
    const approverNama = (approverPegawai && approverPegawai.nama) || (klaim && klaim.approver_nama) || 'Bapak/Ibu Approver'
    const periode = klaim
      ? `${moment(klaim.periode_awal).format('DD/MM/YYYY')} - ${moment(klaim.periode_akhir).format('DD/MM/YYYY')}`
      : '-'
    const total = klaim && klaim.total_keseluruhan != null
      ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(klaim.total_keseluruhan)
      : '-'

    const message = this.generateMessage('klaimapproval', {
      approverNama,
      kodeKlaim: (klaim && klaim.kode_klaim) || '-',
      periode,
      totalKeseluruhan: total,
      approvalUrl: approvalUrl || '',
      makerNama: makerNama || '-',
      facilityName: facilityName || '',
      facilityFooter: facilityFooter || '',
    })

    const phoneCandidate = phone
      || (approverPegawai && (approverPegawai.no_hp || approverPegawai.telepon || approverPegawai.no_wa))
    const resolvedPhone = phoneCandidate ? this.formatPhoneNumberFromLocal(phoneCandidate) : null

    if (!resolvedPhone) {
      return { needsPhoneInput: true, message }
    }

    return this.openWhatsApp(resolvedPhone, message, options)
  }

  /**
   * Get entity data from Pinia auth store (reliable method)
   */
  getEntityData() {
    try {
      const authStore = useAuthStore()
      return authStore.entity || {}
    } catch (error) {
      console.error('Failed to get entity data:', error)
      return {}
    }
  }

  /**
   * Coerce an invoice money field to a number. The API casts these columns to
   * decimal strings ("7500.00"), so a bare truthiness/parseInt check is unsafe.
   */
  toAmount(value) {
    const n = parseFloat(value)
    return Number.isNaN(n) ? 0 : n
  }

  /**
   * Resolve a voucher redemption to a rupiah amount. For a percentage voucher
   * discount_value is the percent, not the money, so it must be applied to the
   * post-discount base and capped by the benefit's max_discount.
   */
  resolveVoucherDiscount(redemption, base) {
    if (!redemption) return 0

    const value = this.toAmount(redemption.discount_value)
    const benefit = redemption.benefit

    if (benefit && benefit.value_type === 'percentage') {
      const maxDiscount = benefit.conditions
        ? this.toAmount(benefit.conditions.max_discount)
        : 0
      let discount = (Math.max(0, base) * value) / 100
      if (maxDiscount > 0) discount = Math.min(discount, maxDiscount)
      return Math.max(0, Math.round(discount))
    }

    return Math.max(0, value)
  }

  /**
   * Render a charge breakdown for the lines between the items and the grand
   * total. Only non-zero lines appear; returns '' when there is nothing to
   * break down (a plain sale with no discounts, fees or rounding).
   */
  formatRincianBiaya(rincian) {
    if (!rincian) return ''

    const subtotal = this.toAmount(rincian.subtotal)
    const diskonItem = this.toAmount(rincian.diskonItem)
    const diskonTotal = this.toAmount(rincian.diskonTotal)
    const voucher = this.toAmount(rincian.voucher)
    const ongkosKirim = this.toAmount(rincian.ongkosKirim)
    const biayaLayanan = this.toAmount(rincian.biayaLayanan)
    const pembulatan = this.toAmount(rincian.pembulatan)

    const lines = []
    if (diskonItem > 0 || diskonTotal > 0 || voucher > 0 || ongkosKirim > 0 || biayaLayanan > 0) {
      lines.push(`Subtotal: ${this.formatMoney(subtotal)}`)
    }
    if (diskonItem > 0) lines.push(`Diskon Item: -${this.formatMoney(diskonItem)}`)
    if (diskonTotal > 0) lines.push(`Diskon: -${this.formatMoney(diskonTotal)}`)
    if (voucher > 0) lines.push(`Voucher: -${this.formatMoney(voucher)}`)
    if (ongkosKirim > 0) lines.push(`Ongkos Kirim: ${this.formatMoney(ongkosKirim)}`)
    if (biayaLayanan > 0) lines.push(`Biaya Layanan: ${this.formatMoney(biayaLayanan)}`)
    if (pembulatan !== 0) lines.push(`Pembulatan: ${this.formatMoney(pembulatan)}`)

    return lines.join('\n')
  }

  /**
   * Extract the charge breakdown straight off an invoice payload.
   */
  buildRincianBiaya(invoice) {
    if (!invoice) return ''

    const detils = invoice.invoicedetil || []
    const subtotal = detils.reduce((sum, item) => sum + this.toAmount(item.total_harga), 0)
    const diskonItem = detils.reduce((sum, item) => sum + Math.max(0, this.toAmount(item.diskon)), 0)
    const diskonTotal = this.toAmount(invoice.diskon_total)

    return this.formatRincianBiaya({
      subtotal,
      diskonItem,
      diskonTotal,
      voucher: this.resolveVoucherDiscount(
        invoice.voucher_redemption,
        subtotal - diskonItem - diskonTotal
      ),
      ongkosKirim: this.toAmount(invoice.ongkos_kirim),
      biayaLayanan: this.toAmount(invoice.biaya_layanan),
      pembulatan: this.toAmount(invoice.pembulatan)
    })
  }

  /**
   * Send struk penjualan (sales receipt) via WhatsApp
   */
  async sendStrukPenjualan(phone, penjualanData, options = {}) {
    try {
      const { invoice, rewardPoints } = penjualanData

      // Get entity data from store (reliable)
      const entityData = this.getEntityData()

      // Build unified footer
      const facilityFooter = this.buildFacilityFooter({
        rewardPoints,
        entityData,
        callerName: 'sendStrukPenjualan'
      })

      // Generate invoice number
      const invoiceNumber = `${moment(invoice.tanggal_pembelian).format('DDMMYYYY')}${String(invoice.no_invoice).padStart(5, '0')}`

      // Format total amount
      const totalAmount = this.formatMoney(invoice.nominal_total)

      // Generate item list. A discounted line shows the original struck
      // through (~...~ is WhatsApp strikethrough) with the net beside it, so
      // the message matches the printed receipt.
      const itemList = invoice.invoicedetil?.map(item => {
        const productName = `${item.id_produk?.nama_produk || 'Produk'} (${item.id_produk?.ukuran || ''})`
        const quantity = this.toAmount(item.jumlah)
        const bruto = this.toAmount(item.total_harga)
        const diskon = Math.max(0, this.toAmount(item.diskon))
        const harga = diskon > 0
          ? `~${this.formatMoney(bruto)}~ ${this.formatMoney(bruto - diskon)}`
          : this.formatMoney(bruto)
        return ` - ${productName} - ${quantity} pcs = ${harga}`
      }).join('\n') || 'Tidak ada item'

      // Prepare message data
      const messageData = {
        facilityName: entityData?.name || 'Klinik',
        invoiceNumber,
        date: moment(invoice.created_at).format('DD/MM/YYYY HH:mm'),
        itemList,
        rincianBiaya: this.buildRincianBiaya(invoice),
        totalAmount,
        facilityFooter
      }

      const message = this.generateMessage('penjualan', messageData)

      return this.openWhatsApp(phone, message, options)
    } catch (error) {
      console.error('Struk Penjualan WhatsApp sending failed:', error)
      throw error
    }
  }

  /**
   * Get service status
   */
  getStatus() {
    return {
      available: this.isWhatsAppAvailable(),
      mobile: this.isMobileDevice(),
      countryCode: this.countryCode,
      templates: Object.keys(this.messageTemplates)
    }
  }
}

export default WhatsAppService